import { useState } from "react";
import Avatar from "@/components/base/Avatar";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { commentsData } from "@/mocks/comments";

interface Comment {
  id: string;
  author: string;
  content: string;
  time: string;
  likes: number;
}

export default function CommentSection({ videoId, onNotify }: { videoId: string; onNotify: (msg: string) => void }) {
  const seed = commentsData[videoId] ?? [];
  const [comments, setComments] = useLocalStorage<Comment[]>(`gg_comments_${videoId}`, seed);
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [likedIds, setLikedIds] = useLocalStorage<string[]>(`gg_comment_likes_${videoId}`, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!author.trim()) {
      setError("请填写你的昵称，方便其他人和你互动。");
      return;
    }
    if (trimmed.length === 0) {
      setError("评论内容不能为空。");
      return;
    }
    if (trimmed.length > 500) {
      setError("评论最多 500 字，请精简一下内容。");
      return;
    }
    const newComment: Comment = {
      id: `local-${Date.now()}`,
      author: author.trim(),
      content: trimmed,
      time: "刚刚",
      likes: 0,
    };
    setComments([newComment, ...comments]);
    setContent("");
    setError("");
    onNotify("评论已发布");
  };

  const toggleLike = (id: string) => {
    setLikedIds(likedIds.includes(id) ? likedIds.filter((x) => x !== id) : [...likedIds, id]);
  };

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-heading font-bold text-lg text-foreground-950">评论互动</h2>
        <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs">{comments.length}</span>
      </div>

      <form onSubmit={submit} className="p-4 rounded-lg bg-background-50 border border-background-200">
        <input
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          maxLength={20}
          placeholder="你的昵称"
          className="w-full h-10 px-3 mb-3 rounded-md bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400"
        />
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="说点什么吧，友善的评论会让社区更美好（最多 500 字）"
          className="w-full px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm outline-none focus:border-primary-400 resize-none"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className={`text-xs ${content.length > 480 ? "text-accent-600" : "text-foreground-400"}`}>
            {content.length}/500
          </span>
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-fill" aria-hidden="true"></i>
            发表评论
          </button>
        </div>
        {error && (
          <p className="mt-2 text-xs text-accent-600 flex items-center gap-1">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
            {error}
          </p>
        )}
      </form>

      <ul className="mt-5 space-y-4">
        {comments.map((c) => {
          const liked = likedIds.includes(c.id);
          return (
            <li key={c.id} className="flex gap-3">
              <Avatar name={c.author} size="md" tone="secondary" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-900">{c.author}</span>
                  <span className="text-xs text-foreground-400">{c.time}</span>
                </div>
                <p className="mt-1 text-sm text-foreground-700 leading-relaxed break-words">{c.content}</p>
                <button
                  type="button"
                  onClick={() => toggleLike(c.id)}
                  className={`mt-2 inline-flex items-center gap-1 text-xs cursor-pointer ${
                    liked ? "text-primary-600" : "text-foreground-400 hover:text-primary-600"
                  }`}
                >
                  <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true"></i>
                  {c.likes + (liked ? 1 : 0)}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}